import { useEffect, useState } from 'react';
import { hasInstallPrompt, isIos, isStandalone, triggerInstall } from '../lib/driverPush';

/**
 * 담당자 웹 설치 안내 — 하단 고정 배너(2026-09-28 way 「관리자 앱에서도 설치할 수 있는 팝업 안내」, DYOPS InstallPrompt 미러).
 *
 * - 🔴 `beforeinstallprompt` stash 는 `lib/driverPush.ts` **하나에만** 둔다(두 곳에서 잡으면 먼저 잡은 쪽만 받는다 — DYOPS 실측).
 *   이름은 driver 지만 모듈 로드 즉시 잡으므로 담당자 웹에서도 그대로 쓴다. 이벤트 이름도 같다.
 * - 설치 대상은 `admin.webmanifest`(index.html 이 /driver 밖에서 붙인다) + `admin-sw.js`(아래에서 등록).
 * - 이미 앱으로 열었거나 「닫기」를 눌렀으면 안 뜬다. 닫기는 이 브라우저에만 기억한다.
 * - iPhone·iPad 는 자동 설치가 없어 Safari 「공유 → 홈 화면에 추가」를 글로 안내한다.
 */

const DISMISS_KEY = 'businweb.admin.install-dismissed';

function readDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === '1';
  } catch {
    return false;
  }
}

let swStarted = false;
function registerAdminSw() {
  if (swStarted || !('serviceWorker' in navigator)) return;
  swStarted = true;
  navigator.serviceWorker.register('/admin-sw.js', { scope: '/' }).catch(() => {
    /* 워커가 안 걸리면 설치 버튼 대신 메뉴 안내가 뜰 뿐 — 업무 화면과 무관 */
  });
}

export default function InstallPrompt() {
  const [installable, setInstallable] = useState(() => hasInstallPrompt());
  const [installed, setInstalled] = useState(() => isStandalone());
  const [dismissed, setDismissed] = useState(readDismissed);
  const [menuHint, setMenuHint] = useState(false);

  useEffect(() => {
    registerAdminSw();
    const onInstallable = () => setInstallable(true);
    const onInstalled = () => setInstalled(true);
    window.addEventListener('driver:installable', onInstallable);
    window.addEventListener('driver:installed', onInstalled);
    return () => {
      window.removeEventListener('driver:installable', onInstallable);
      window.removeEventListener('driver:installed', onInstalled);
    };
  }, []);

  if (installed || dismissed) return null;
  const ios = isIos();

  async function install() {
    const r = await triggerInstall();
    if (r === 'unavailable') setMenuHint(true);
    else setInstallable(false);
  }

  function dismiss() {
    setDismissed(true);
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* 저장 못 하면 다음에 한 번 더 뜰 뿐 */
    }
  }

  return (
    <div className="pointer-events-none fixed inset-x-0 bottom-[calc(var(--bottom-bar)+1rem)] z-40 flex justify-center px-3" role="region" aria-label="앱 설치 안내">
      <div className="pointer-events-auto flex w-full max-w-xl items-center gap-3 rounded-2xl border border-brand-100 bg-white px-4 py-3 shadow-lg">
        <img src="/admin-icon-192.png" alt="" className="h-10 w-10 flex-shrink-0 rounded-xl" />
        <div className="min-w-0 flex-1">
          <p className="break-keep text-sm font-bold text-ink">버스인을 앱으로 설치하면 바탕화면·홈 화면에서 바로 열 수 있습니다</p>
          <p className="mt-0.5 break-keep text-xs text-ink-soft">
            {ios
              ? 'Safari 의 공유(⬆) → 「홈 화면에 추가」를 누르세요. iPhone·iPad 는 자동 설치가 없습니다.'
              : menuHint
                ? '브라우저 주소창 오른쪽의 설치 아이콘(⊕) 또는 메뉴(⋮) → 「앱 설치」를 누르세요.'
                : installable
                  ? '「설치」를 누르면 바로 설치됩니다. 설치 후에도 로그인·화면은 똑같습니다.'
                  : '크롬·엣지에서는 주소창 오른쪽의 설치 아이콘(⊕) 또는 메뉴(⋮) → 「앱 설치」로 설치할 수 있습니다.'}
          </p>
        </div>
        {!ios && (
          <button type="button" className="btn-primary flex-shrink-0 whitespace-nowrap" onClick={() => void install()}>
            설치
          </button>
        )}
        <button
          type="button"
          onClick={dismiss}
          aria-label="설치 안내 닫기"
          className="flex-shrink-0 self-start px-1 text-ink-faint hover:text-red-600"
        >
          ✕
        </button>
      </div>
    </div>
  );
}
